import { CSSProperties, ReactNode } from 'react';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { LegacyColumn } from './MatDataGridTypes';

/**
 * Type guard to check if columns are legacy format
 */
export function isLegacyColumn(column: any): column is LegacyColumn {
  return 'title' in column && !('accessorKey' in column) && !('header' in column);
}

/**
 * Type guard to check if columns array contains legacy columns
 */
export function isLegacyColumnArray(columns: any[]): columns is LegacyColumn[] {
  return columns.length > 0 && isLegacyColumn(columns[0]);
}

/**
 * Convert legacy material-table column to Material React Table column definition
 */
// Columns that should have hidden header text
const HIDDEN_HEADER_COLUMNS = ['imagePath', 'actions'];

export function convertLegacyColumn<TData extends Record<string, any>>(
  legacyColumn: LegacyColumn
): MRT_ColumnDef<TData> {
  // Hide header text for specific columns (Image, Actions)
  const shouldHideHeader = HIDDEN_HEADER_COLUMNS.includes(legacyColumn.field || '');

  const column: MRT_ColumnDef<TData> = {
    header: shouldHideHeader ? '' : legacyColumn.title,
    enableSorting: legacyColumn.sorting !== false,
  };

  // Handle nested field paths (e.g., 'vendor.vendorCompanyId')
  if (legacyColumn.field && legacyColumn.field.includes('.')) {
    const fieldPath = legacyColumn.field.split('.');
    column.accessorFn = (row: TData) => {
      let value: any = row;
      for (const key of fieldPath) {
        value = value?.[key];
        if (value === undefined) return undefined;
      }
      return value;
    };
    column.id = legacyColumn.field;
  } else {
    column.accessorKey = (legacyColumn.field || `col_${legacyColumn.title}`) as keyof TData & string;
  }

  // Handle size/width
  if (legacyColumn.width !== undefined) {
    const width = typeof legacyColumn.width === 'string'
      ? parseInt(legacyColumn.width, 10)
      : legacyColumn.width;
    if (!isNaN(width)) {
      column.size = width;
    }
  }

  if (legacyColumn.minWidth !== undefined) {
    const minWidth = typeof legacyColumn.minWidth === 'string'
      ? parseInt(legacyColumn.minWidth, 10)
      : legacyColumn.minWidth;
    if (!isNaN(minWidth)) {
      column.minSize = minWidth;
    }
  }

  // Handle custom render function
  if (legacyColumn.render) {
    column.Cell = ({ row }: { row: MRT_Row<TData> }) => {
      return legacyColumn.render!(row.original) as ReactNode;
    };
  }

  // Handle cell styling
  if (legacyColumn.cellStyle) {
    if (typeof legacyColumn.cellStyle === 'function') {
      column.muiTableBodyCellProps = ({ row }: { row: MRT_Row<TData> }) => {
        const cellValue = legacyColumn.field ? row.original[legacyColumn.field as keyof TData] : undefined;
        const style = (legacyColumn.cellStyle as Function)(cellValue, row.original);
        return { sx: style };
      };
    } else {
      column.muiTableBodyCellProps = {
        sx: legacyColumn.cellStyle as CSSProperties,
      };
    }
  }

  // Handle header styling
  if (legacyColumn.headerStyle) {
    column.muiTableHeadCellProps = {
      sx: legacyColumn.headerStyle,
    };
  }

  // Handle alignment
  if (legacyColumn.align) {
    column.muiTableHeadCellProps = {
      ...column.muiTableHeadCellProps,
      align: legacyColumn.align,
    };
    column.muiTableBodyCellProps = {
      ...column.muiTableBodyCellProps,
      align: legacyColumn.align,
    } as any;
  }

  return column;
}

/**
 * Convert array of legacy columns to Material React Table columns
 */
export function convertLegacyColumns<TData extends Record<string, any>>(
  legacyColumns: LegacyColumn[]
): MRT_ColumnDef<TData>[] {
  return legacyColumns
    .filter(col => !col.isHeading && !col.isRemoved) // Filter out heading rows and removed columns
    .map(col => convertLegacyColumn<TData>(col));
}

/**
 * Get initial column visibility state from legacy columns
 */
export function getColumnVisibility(columns: LegacyColumn[]): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};

  columns.forEach(col => {
    if (col.field && col.hidden !== undefined) {
      visibility[col.field] = !col.hidden;
    }
  });

  return visibility;
}

/**
 * Get initial sorting state from legacy columns
 */
export function getInitialSorting(columns: LegacyColumn[]): { id: string; desc: boolean }[] {
  const sortedColumn = columns.find(col => col.defaultSort && col.field);

  if (sortedColumn && sortedColumn.field) {
    return [{
      id: sortedColumn.field,
      desc: sortedColumn.defaultSort === 'desc',
    }];
  }

  return [];
}

/**
 * Convert MRT sorting state to legacy format for onOrderChange callback
 */
export function convertSortingToLegacy(
  sorting: { id: string; desc: boolean }[],
  columns: LegacyColumn[]
): { columnId: number; direction: 'asc' | 'desc' | '' } | null {
  if (sorting.length === 0) {
    return null;
  }

  const sort = sorting[0];
  const columnIndex = columns.findIndex(col => col.field === sort.id);

  return {
    columnId: columnIndex,
    direction: sort.desc ? 'desc' : 'asc',
  };
}

/**
 * Ensure columns are in MRT format
 */
export function ensureMRTColumns<TData extends Record<string, any>>(
  columns: LegacyColumn[] | MRT_ColumnDef<TData>[]
): MRT_ColumnDef<TData>[] {
  if (isLegacyColumnArray(columns)) {
    return convertLegacyColumns<TData>(columns);
  }
  return columns as MRT_ColumnDef<TData>[];
}
